/**
 * Per-vane wind drift coefficient table.
 *
 * Layered fallback strategy:
 *   1. Vane catalog entry's explicit windDriftCoefficient field (highest priority)
 *   2. This name-keyed table — measured values from PCA 2025 + engineering
 *      estimates for non-PCA-tested vanes
 *   3. Airfoil tier default in getVaneDriftCoefficient()
 *
 * Baseline: Bohning Blazer 3-fletch 2° helical = 1.000.
 *
 * Sources:
 *   - PCA 2025 Arrow Ballistics Study (James Yates), CC BY-NC-SA 4.0
 *     https://www.precisioncutarchery.com/research/arrow-study-2025
 *   - Firenock Aerovane FAQ: https://firenock.com/aerovane-faq/
 *
 * Values labeled `pca: true` come from the PCA 2025 measured drift @ 120yd
 * column, normalized to Blazer = 21.49" = 1.000. Values labeled `pca: false`
 * are engineering estimates from frontal area (height × length) + airfoil
 * mechanism.
 */
import type { VaneAirfoilTier } from '@/types';

export interface VaneDriftEntry {
  coefficient: number;
  airfoilTier: VaneAirfoilTier;
  pca: boolean;
}

export const VANE_DRIFT_MAP: Record<string, VaneDriftEntry> = {
  // ──────────────────────────────────────────────────────────
  // PCA 2025 MEASURED — 3-fletch, 2° helical, baseline configuration
  // ──────────────────────────────────────────────────────────
  'Flex Fletch FFP-187':       { coefficient: 0.863, airfoilTier: 'low-profile', pca: true },
  'AAE Max 2.0 Shield Cut':    { coefficient: 0.874, airfoilTier: 'low-profile', pca: true },
  'AAE Max 23':                { coefficient: 0.894, airfoilTier: 'low-profile', pca: true },
  'TAC Driver 2.25':           { coefficient: 0.919, airfoilTier: 'low-profile', pca: true },
  'DCA Super Sabre':           { coefficient: 0.935, airfoilTier: 'low-profile', pca: true },
  'Flex Fletch SK2':           { coefficient: 0.936, airfoilTier: 'low-profile', pca: true },
  'TAC Driver 2.75':           { coefficient: 0.949, airfoilTier: 'low-profile', pca: true },
  'Q2I Griff X 3.0':           { coefficient: 0.955, airfoilTier: 'standard',    pca: true },
  'Bohning Alpha':             { coefficient: 0.973, airfoilTier: 'standard',    pca: true },
  'Flex Fletch Quad X':        { coefficient: 0.972, airfoilTier: 'low-profile', pca: true },
  'AAE Hybrid HP':             { coefficient: 0.984, airfoilTier: 'low-profile', pca: true },
  'AAE Hybrid 26':             { coefficient: 0.984, airfoilTier: 'low-profile', pca: true },
  'AAE Airrzr 26':             { coefficient: 0.986, airfoilTier: 'low-profile', pca: true },
  'Q2I Fusion X-II 2.5':       { coefficient: 0.986, airfoilTier: 'low-profile', pca: true },
  'Flex Fletch FFP-360':       { coefficient: 0.989, airfoilTier: 'standard',    pca: true },
  'Bohning Atlas':             { coefficient: 0.993, airfoilTier: 'standard',    pca: true },
  'AAE Max Stealth':           { coefficient: 0.993, airfoilTier: 'low-profile', pca: true },
  'Flex Fletch FFP-360X':      { coefficient: 1.000, airfoilTier: 'standard',    pca: true },
  'Bohning Blazer':            { coefficient: 1.000, airfoilTier: 'standard',    pca: true },  // BASELINE
  'Q2I Predator X':            { coefficient: 1.014, airfoilTier: 'standard',    pca: true },
  'Iron Will 2.5 Vane':        { coefficient: 1.019, airfoilTier: 'standard',    pca: true },
  'AAE Airrzr Talon':          { coefficient: 1.018, airfoilTier: 'standard',    pca: true },
  'AAE Max Hunter':            { coefficient: 1.020, airfoilTier: 'standard',    pca: true },
  'FF Silent Knight 3"':       { coefficient: 1.018, airfoilTier: 'standard',    pca: true },
  'Q2i Duravane DV-X 3.0':     { coefficient: 1.024, airfoilTier: 'standard',    pca: true },

  // ──────────────────────────────────────────────────────────
  // ESTIMATED — physics-based, not in PCA dataset
  // ──────────────────────────────────────────────────────────

  // Firenock Aerovane — airfoil mechanism, smaller frontal area, 30× spin.
  // Firenock claims lower "Cross Wind Signature." Per their published spin
  // numbers (AV3 = 450 RPS @ 300 fps) the boundary-layer / Magnus mechanism
  // is real; engineering estimate slots both below Blazer.
  'Firenock AV3':              { coefficient: 0.82, airfoilTier: 'airfoil', pca: false },
  'Firenock AV2':              { coefficient: 0.88, airfoilTier: 'airfoil', pca: false },
  'Firenock Aerovane III':     { coefficient: 0.82, airfoilTier: 'airfoil', pca: false },
  'Firenock Aerovane II':      { coefficient: 0.88, airfoilTier: 'airfoil', pca: false },

  // Bohning X-vane family — smaller-frontal-area cousins of the Blazer.
  // Estimated proportionally from H × L vs Blazer (0.53 × 2.0 = 1.06 in²).
  'Bohning Blazer X2':         { coefficient: 0.94, airfoilTier: 'low-profile', pca: false },
  'Bohning X Vane 1.5"':       { coefficient: 0.87, airfoilTier: 'low-profile', pca: false },
  'Bohning X Vane 1.75"':      { coefficient: 0.90, airfoilTier: 'low-profile', pca: false },
  'Bohning X Vane 2.25"':      { coefficient: 0.95, airfoilTier: 'low-profile', pca: false },
  'Bohning X Vane 3.0"':       { coefficient: 1.02, airfoilTier: 'standard',    pca: false },
  'Bohning X3 Vane 2.25"':     { coefficient: 0.95, airfoilTier: 'low-profile', pca: false },

  // Bohning hunter-class
  'Bohning Heat':              { coefficient: 1.00, airfoilTier: 'standard',    pca: false },

  // AAE other entries — extrapolated from PCA-tested family members
  'AAE Hybrid 16':             { coefficient: 0.85, airfoilTier: 'low-profile', pca: false },
  'AAE Hybrid 1.85 Shield':    { coefficient: 0.87, airfoilTier: 'low-profile', pca: false },
  'AAE Hybrid 2.0 Shield':     { coefficient: 0.88, airfoilTier: 'low-profile', pca: false },
  'AAE Hybrid 23':             { coefficient: 0.92, airfoilTier: 'low-profile', pca: false },
  'AAE Hybrid 4.0':            { coefficient: 1.08, airfoilTier: 'standard',    pca: false },
  'AAE Plastifletch Max 2.0':  { coefficient: 0.99, airfoilTier: 'standard',    pca: false },
  'AAE Plastifletch Max 23':   { coefficient: 1.01, airfoilTier: 'standard',    pca: false },
  'AAE Pro Stealth':           { coefficient: 0.99, airfoilTier: 'standard',    pca: false },
  'AAE Pro Max':               { coefficient: 1.05, airfoilTier: 'standard',    pca: false },
  'AAE EP23':                  { coefficient: 0.96, airfoilTier: 'low-profile', pca: false },
  'AAE EP26':                  { coefficient: 0.99, airfoilTier: 'standard',    pca: false },
  'AAE EP40':                  { coefficient: 1.06, airfoilTier: 'standard',    pca: false },
  'AAE Trad Vane':             { coefficient: 0.97, airfoilTier: 'standard',    pca: false },

  // TAC (floutdoors.com) — estimated from the two PCA-measured TAC Driver
  // anchors (2.25 = 0.919, 2.75 = 0.949 → ~0.06/inch of length). Driver +
  // Matrix are low-profile; High Profile + Summit carry more frontal area
  // (taller) so they slot at/above the Blazer baseline. pca:false = estimate.
  'TAC Driver 1.75':           { coefficient: 0.89, airfoilTier: 'low-profile', pca: false },
  'TAC Driver 2.00':           { coefficient: 0.90, airfoilTier: 'low-profile', pca: false },
  'TAC Driver 3.75':           { coefficient: 1.01, airfoilTier: 'low-profile', pca: false },
  'TAC Driver High Profile 2.25': { coefficient: 1.02, airfoilTier: 'standard', pca: false },
  'TAC Matrix 1.75':           { coefficient: 0.89, airfoilTier: 'low-profile', pca: false },
  'TAC Matrix 2.00':           { coefficient: 0.91, airfoilTier: 'low-profile', pca: false },
  'TAC Matrix 2.25':           { coefficient: 0.92, airfoilTier: 'low-profile', pca: false },
  'TAC Summit 1.95':           { coefficient: 0.96, airfoilTier: 'standard',    pca: false },

  // UltraView
  'Ultraview UV Vane':         { coefficient: 0.99, airfoilTier: 'low-profile', pca: false },

  // Q2i family — extrapolated
  'Q2i Fusion':                { coefficient: 0.99, airfoilTier: 'low-profile', pca: false },

  // Easton Diamond — known as low-profile competition vanes (PCA didn't test).
  'Easton Diamond 175':        { coefficient: 0.91, airfoilTier: 'low-profile', pca: false },
  'Easton Diamond 235':        { coefficient: 0.96, airfoilTier: 'low-profile', pca: false },
  'Easton Diamond 280':        { coefficient: 1.00, airfoilTier: 'standard',    pca: false },

  // Gateway — known parabolic/feather-replacement vanes (not PCA-tested).
  'Gateway Parabolic 3.0':     { coefficient: 1.05, airfoilTier: 'standard',    pca: false },
  'Gateway Spider 3.0':        { coefficient: 1.02, airfoilTier: 'standard',    pca: false },
};

/**
 * Look up by exact name first; if not found, try fuzzy match by stripping
 * trailing quotes and normalising whitespace.
 */
export function lookupVaneDriftEntry(vaneName: string | undefined): VaneDriftEntry | null {
  if (!vaneName) return null;
  if (VANE_DRIFT_MAP[vaneName]) return VANE_DRIFT_MAP[vaneName];
  const normalised = vaneName.replace(/["\s]+/g, ' ').trim();
  for (const [k, v] of Object.entries(VANE_DRIFT_MAP)) {
    if (k.replace(/["\s]+/g, ' ').trim() === normalised) return v;
  }
  return null;
}
